import {
	Card,
	CardContent,
	CardHeader,
	IconButton,
	Stack,
	Typography,
	Chip,
} from '@mui/material';

import { useEffect, useState } from 'react';

import { ReactComponent as RegInventChipIcon } from 'common/assets/images/closeBoxIcon.svg';
import { ReactComponent as RegIcon } from 'common/assets/images/recipe_reg_obg.svg';
import { Loader } from 'common/components/Loader/Loader';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import {
	AI_CARD_CONTENT,
	BUILDWORKFLOW_CARD_COUNT,
	CURRENTINV_CARD_COUNT,
	REG_INV_CARD_TYPE,
} from 'common/constants/RegulationInventoryConstants';
import { toSentenceCase, formatNumber } from 'common/utils/utilFunctions';

import { getCurrentInventoryCardCount } from 'features/RegulationInventory/api';
import {
	IBuildCard,
	ICurrentInventoryRequestHeaders,
	IRegulatoryInventoryCard,
} from 'features/RegulationInventory/api/type-def';

import inventoryCardStyle from './InventoryCard.module.scss';

const { NO_DATA_SEARCH } = AI_CARD_CONTENT;
const { OK } = HTTP_STATUS_CODES;

const { NOT_STARTED, APPLICATION, GROUPING, SUMMARY, MAPPING } =
	BUILDWORKFLOW_CARD_COUNT;

const {
	OBLIGATION,
	REGULATION,
	ASSIGNED,
	BUSINESS_UNIT,
	PROCESS,
	RISK,
	CONTROL,
	POLICY,
} = CURRENTINV_CARD_COUNT;

const { INVENTORY } = REG_INV_CARD_TYPE;
interface IResponseModel {
	text: string;
	count: string;
}

interface IInventoryCard {
	key: string;
	title: string;
	handleClick: any;
	apiHeaders?: {
		loggedInUser?: string;
		jurisdiction?: string;
		issuanceType: string[];
	};
	cardType?: REG_INV_CARD_TYPE;
}

const InventoryCard = (props: IInventoryCard) => {
	const { key, title, handleClick, cardType = 'inventory', apiHeaders } = props;

	const [response, setResponse] = useState<IResponseModel[]>([]);
	const [loading, setIsLoading] = useState<boolean>(false);
	const [error, setIsError] = useState<boolean>(false);

	const getCardDetails = async () => {
		let headers: ICurrentInventoryRequestHeaders;
		if (cardType === 'inventory') {
			headers = {
				loggedInUser: apiHeaders?.loggedInUser || '',
				jurisdiction: apiHeaders?.jurisdiction || '',
				issuanceType: apiHeaders?.issuanceType || [],
			};
		} else {
			headers = {
				jurisdiction: apiHeaders?.jurisdiction || '',
				issuanceType: apiHeaders?.issuanceType || [],
			};
		}
		setIsLoading(true);
		const resp: any = await getCurrentInventoryCardCount(headers);
		if (resp?.statusCode === OK) {
			setIsLoading(false);
			if (cardType === 'inventory') {
				const {
					obligationsCount,
					regulationCount,
					assignedToMeCount,
					businessUnitCount,
					processCount,
					riskCount,
					controlCount,
					policyCount,
				} = resp?.responseBody as IRegulatoryInventoryCard;
				const response = [
					{
						text: OBLIGATION,
						count: obligationsCount,
					},
					{
						text: REGULATION,
						count: regulationCount,
					},
					{
						text: ASSIGNED,
						count: assignedToMeCount,
					},
					{
						text: BUSINESS_UNIT,
						count: businessUnitCount,
					},
					{
						text: PROCESS,
						count: processCount,
					},
					{
						text: RISK,
						count: riskCount,
					},
					{
						text: CONTROL,
						count: controlCount,
					},
					{
						text: POLICY,
						count: policyCount,
					},
				];
				setResponse(response);
			} else {
				const {
					notStartedCount,
					applicabilityCount,
					groupingCount,
					mappingCount,
					summaryCount,
				} = resp?.responseBody as IBuildCard;
				const response = [
					{
						text: NOT_STARTED,
						count: notStartedCount,
					},
					{
						text: APPLICATION,
						count: applicabilityCount,
					},
					{
						text: GROUPING,
						count: groupingCount,
					},
					{
						text: SUMMARY,
						count: summaryCount,
					},
					{
						text: MAPPING,
						count: mappingCount,
					},
				];
				setResponse(response);
			}
		} else {
			setIsLoading(false);
			setIsError(true);
		}
	};

	useEffect(() => {
		getCardDetails();
	}, []);

	return (
		<Card
			key={key}
			data-testid="current-inventory-card"
			className={`${inventoryCardStyle['inventorycard-container']} ${
				cardType === INVENTORY
					? inventoryCardStyle.inventory
					: inventoryCardStyle.workflow
			}`}
			onClick={handleClick}
		>
			<Stack
				direction="column"
				justifyContent="space-between"
				className={inventoryCardStyle['inventorycard-container-stack']}
			>
				<CardHeader
					className={inventoryCardStyle['inventorycard-header']}
					title={
						<Stack
							direction="row"
							className={inventoryCardStyle['inventorycard-title-container']}
						>
							<IconButton
								className={inventoryCardStyle['inventorycard-icon']}
								disableRipple
							>
								<RegIcon />
							</IconButton>
							<Typography className={inventoryCardStyle['inventorycard-title']}>
								{toSentenceCase(title)}
							</Typography>
						</Stack>
					}
				/>
				{loading && (
					<div className={inventoryCardStyle['loader-position']}>
						<Loader show />
					</div>
				)}
				{error && !loading ? (
					<div className={inventoryCardStyle['no-data-message']}>
						{NO_DATA_SEARCH}
					</div>
				) : (
					<CardContent className={inventoryCardStyle['inventorycard-content']}>
						{response.map((ele) => (
							<Chip
								key={ele.text}
								icon={<RegInventChipIcon />}
								label={`${toSentenceCase(ele.text)}: ${formatNumber(
									Number(ele.count),
								)}`}
								variant="outlined"
								sx={{ mr: 0.5, mb: 0.5 }}
							/>
						))}
					</CardContent>
				)}
			</Stack>
		</Card>
	);
};

export default InventoryCard;
